import React, { useState, useRef } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  Button,
  IconButton,
  LinearProgress,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Assignment,
  LocationOn,
  CheckCircle,
  Warning,
  Add,
  Delete,
  Info,
  Save,
  CloudUpload,
  Print,
} from '@mui/icons-material';
import { Checkbox, FormControlLabel, Stack } from '@mui/material';
import { useBundleCreation } from '../../contexts/BundleCreationContext';
import { useShelfWiseInventory } from '../../hooks/useWarehouseData';
import { WarehouseAPI } from '../../services/api';
import { getAggregatedChildSKUs } from '../../utils/inventoryUtils';
import { newOperationId } from '../../utils/operationId';
import {
  remainingOnShelf,
  totalRequired as computeTotalRequired,
  stillNeeded as computeStillNeeded,
  clampToAvailableAndNeeded,
} from '../../utils/childSkuPick';
import { useToast } from '../common/useToast';

interface SKUOption {
  label: string;
  value: string;
  stillNeeded: number;
}

interface ShelfOption {
  label: string;
  value: string;
  quantity: number;
}

const ChildSKUPicker: React.FC = () => {
  const { state, dispatch, saveDraft } = useBundleCreation();
  const toast = useToast();

  const aggregatedChildSKUs = React.useMemo(() =>
    state.selectedBundleSKU ? getAggregatedChildSKUs(state.selectedBundleSKU.childSKUs) : []
    , [state.selectedBundleSKU]);

  const [selectedSKU, setSelectedSKU] = useState<SKUOption | null>(null);
  const [selectedShelf, setSelectedShelf] = useState<ShelfOption | null>(null);
  const [quantityToPick, setQuantityToPick] = useState<string>('1');
  const [syncingToUnicommerce, setSyncingToUnicommerce] = useState(false);
  // Synchronous lock + per-action id (state alone can't stop a same-tick double invocation).
  const syncInFlightRef = useRef(false);
  const syncOperationIdRef = useRef<string | null>(null);
  const [unicommerceSyncCompleted, setUnicommerceSyncCompleted] = useState(state.unicommerceSyncCompleted);
  const [showUnicommerceModal, setShowUnicommerceModal] = useState(false);
  const [includeBundleName, setIncludeBundleName] = useState(true);
  const [includePickerName, setIncludePickerName] = useState(true);
  const [pickerName, setPickerName] = useState<string>(() => {
    try {
      const last = window.localStorage.getItem('lastPickerName');
      if (last) return last;
      const stored = window.localStorage.getItem('bundleManagerUser');
      if (stored) return JSON.parse(stored).name || '';
    } catch {
      // ignore parse errors
    }
    return '';
  });
  const [unicommerceResult, setUnicommerceResult] = useState<{
    success: boolean;
    message: string;
    details: string;
    adjustedCount: number;
  } | null>(null);

  // Fetch shelf inventory for the selected SKU
  const { data: shelfInventoryData, isLoading: isLoadingShelfData } = useShelfWiseInventory(
    selectedSKU?.value || '',
    0,
    1000 // Large limit to get all shelves
  );

  // Sync with global state for resumption
  React.useEffect(() => {
    setUnicommerceSyncCompleted(state.unicommerceSyncCompleted);
  }, [state.unicommerceSyncCompleted]);

  // Ensure we have a selected bundle and validation result.
  // If we're resuming a draft that's already at the picking stage, we can be more lenient
  // if state.childSKUPicking has data, which indicates validation was previously passed.
  const hasPickingData = Object.keys(state.childSKUPicking).length > 0;
  if (!state.selectedBundleSKU || (!state.validationResult?.isValid && !hasPickingData)) {
    return (
      <Alert severity="warning">
        Please complete stock validation first.
      </Alert>
    );
  }

  // Get available shelves for the selected SKU from API data
  const getAvailableShelvesForSKU = (): ShelfOption[] => {

    if (!selectedSKU || !shelfInventoryData?.data) {
      return [];
    }

    // CASE-INSENSITIVE MATCHING: Filter items where skuCode matches the selected SKU
    const goodInventoryItems = shelfInventoryData.data
      .filter(item => {
        const matches = item.inventoryType === 'GOOD_INVENTORY' &&
          item.skuCode.toLowerCase() === selectedSKU.value.toLowerCase(); // Case-insensitive match

        return matches;
      });


    // Filter out shelves where all quantity has already been picked
    const availableShelves = goodInventoryItems.filter(item => {
      // Check if this shelf has already been picked from
      const picking = state.childSKUPicking[selectedSKU.value];
      if (!picking || !picking.shelfPickings) return true; // No pickings yet, shelf is available

      // Find picking from this specific shelf
      const shelfPicking = picking.shelfPickings.find(sp => sp.shelfLocation === item.shelfCode);
      if (!shelfPicking) return true; // No picking from this shelf yet, it's available

      // Calculate remaining quantity on this shelf
      const remainingQuantity = remainingOnShelf(item.quantity, shelfPicking.quantityToPick);

      return remainingQuantity > 0; // Only show if there's still quantity available
    });

    return availableShelves.map(item => {
      // Calculate and show remaining quantity in the label
      const picking = state.childSKUPicking[selectedSKU.value];
      const shelfPicking = picking?.shelfPickings?.find(sp => sp.shelfLocation === item.shelfCode);
      const remainingQuantity = shelfPicking ? remainingOnShelf(item.quantity, shelfPicking.quantityToPick) : item.quantity;

      return {
        label: `${item.shelfCode} (${remainingQuantity} available)`,
        value: item.shelfCode,
        quantity: remainingQuantity, // Use remaining quantity instead of original
      };
    });
  };

  // Get all SKUs that still need picking
  const getAvailableSKUs = (): SKUOption[] => {
    return aggregatedChildSKUs.map((childSKU: any) => {
      const picking = state.childSKUPicking[childSKU.skuId];
      const needed = picking
        ? computeStillNeeded(picking.totalRequired, picking.totalPicked)
        : computeTotalRequired(childSKU.requiredQuantity, state.quantity);

      return {
        label: `${childSKU.name} (${needed} needed)`,
        value: childSKU.skuId,
        stillNeeded: needed,
      };
    }).filter((item: any) => item.stillNeeded > 0);
  };

  const handleAddShelfPicking = () => {
    const quantity = parseInt(quantityToPick) || 1;
    if (!selectedSKU || !selectedShelf || quantity <= 0) return;

    const skuId = selectedSKU.value;
    const shelfLocation = selectedShelf.value;

    // Find the selected shelf data from the current API response
    const selectedShelfData = shelfInventoryData?.data?.find(
      item => item.shelfCode === shelfLocation && item.inventoryType === 'GOOD_INVENTORY'
    );

    if (!selectedShelfData) return;

    // Calculate how much is still needed for this SKU
    const picking = state.childSKUPicking[skuId];
    const childSKU = aggregatedChildSKUs.find((sku: any) => sku.skuId === skuId);
    const totalRequired = childSKU ? computeTotalRequired(childSKU.requiredQuantity, state.quantity) : 0;
    const totalPicked = picking ? picking.totalPicked : 0;
    const stillNeeded = computeStillNeeded(totalRequired, totalPicked);

    // Don't allow picking more than needed
    if (stillNeeded <= 0) {
      toast.warning(`No more ${childSKU?.name || 'items'} needed. Required: ${totalRequired}, Already picked: ${totalPicked}`);
      return;
    }

    // Limit quantity to what's available on shelf AND what's still needed
    const actualQuantityToPick = clampToAvailableAndNeeded(quantity, selectedShelfData.quantity, stillNeeded);

    // Check if we already have a picking from this shelf
    const existingPicking = state.childSKUPicking[skuId]?.shelfPickings.find(
      sp => sp.shelfLocation === shelfLocation
    );

    if (existingPicking) {
      // Update existing picking
      dispatch({
        type: 'UPDATE_SHELF_PICKING',
        payload: {
          skuId: skuId,
          shelfLocation: shelfLocation,
          quantityToPick: actualQuantityToPick
        }
      });
    } else {
      // Add new picking
      dispatch({
        type: 'ADD_SHELF_PICKING',
        payload: {
          skuId: skuId,
          shelfLocation: shelfLocation,
          quantityToPick: actualQuantityToPick,
          availableQuantity: selectedShelfData.quantity
        }
      });
    }

    // Reset form
    setSelectedSKU(null);
    setSelectedShelf(null);
    setQuantityToPick('1');
  };

  const handleRemoveShelfPicking = (skuId: string, shelfLocation: string) => {
    dispatch({
      type: 'REMOVE_SHELF_PICKING',
      payload: { skuId, shelfLocation }
    });
  };

  const handleQuantityChange = (skuId: string, shelfLocation: string, newQuantity: number) => {
    // Calculate how much is still needed for this SKU (excluding the current picking being edited)
    const picking = state.childSKUPicking[skuId];
    const childSKU = aggregatedChildSKUs.find((sku: any) => sku.skuId === skuId);
    const totalRequired = childSKU ? computeTotalRequired(childSKU.requiredQuantity, state.quantity) : 0;

    // Calculate total picked excluding the current shelf being edited
    const totalPickedExcludingCurrent = picking ?
      picking.shelfPickings
        .filter(sp => sp.shelfLocation !== shelfLocation)
        .reduce((sum, sp) => sum + sp.quantityToPick, 0) : 0;

    const maxAllowedForThisShelf = totalRequired - totalPickedExcludingCurrent;

    dispatch({
      type: 'UPDATE_SHELF_PICKING',
      payload: {
        skuId,
        shelfLocation,
        quantityToPick: Math.max(0, Math.min(newQuantity, maxAllowedForThisShelf))
      }
    });
  };

  const getProgressForSKU = (skuId: string) => {
    const picking = state.childSKUPicking[skuId];
    if (!picking || picking.totalRequired === 0) return 0;
    return Math.min(100, (picking.totalPicked / picking.totalRequired) * 100);
  };

  const allRequiredPicked = aggregatedChildSKUs.every((childSKU: any) => {
    const picking = state.childSKUPicking[childSKU.skuId];
    return picking && picking.totalPicked >= picking.totalRequired;
  });

  // Function to sync picked SKUs to Unicommerce
  const handleSyncToUnicommerce = async () => {
    if (syncingToUnicommerce || syncInFlightRef.current) return; // guard against re-entrancy
    if (!allRequiredPicked) {
      toast.warning('Please complete all SKU picking before syncing to Unicommerce.');
      return;
    }

    syncInFlightRef.current = true;
    // One id per sync action, reused if it is retried after a partial failure so lines
    // already deducted are replayed instead of deducted a second time. Cleared on success.
    if (!syncOperationIdRef.current) syncOperationIdRef.current = newOperationId();
    const syncOperationId = syncOperationIdRef.current;
    setSyncingToUnicommerce(true);
    try {
      // Prepare inventory adjustments data based on picked SKUs
      // Deduplicate by SKU + shelf to avoid sending duplicate adjustments
      const adjustmentsMap: Record<string, { skuCode: string; shelfCode: string; quantity: number }> = {};

      Object.entries(state.childSKUPicking).forEach(([skuId, picking]) => {
        if (picking.picked && picking.shelfPickings.length > 0) {
          picking.shelfPickings.forEach(shelfPicking => {
            const key = `${skuId}::${shelfPicking.shelfLocation}`;
            const qty = -Math.abs(shelfPicking.quantityToPick || 0); // negative for deduction
            if (!adjustmentsMap[key]) {
              adjustmentsMap[key] = { skuCode: skuId, shelfCode: shelfPicking.shelfLocation, quantity: qty };
            } else {
              // Sum quantities if multiple entries exist for same SKU+shelf
              adjustmentsMap[key].quantity += qty;
            }
          });
        }
      });

      const inventoryAdjustments = Object.values(adjustmentsMap).map(a => ({
        skuCode: a.skuCode,
        shelfCode: a.shelfCode,
        quantity: a.quantity,
        inventoryType: 'GOOD_INVENTORY',
        operation: 'SUBTRACT'
      }));

      // If after dedupe there's nothing to send, abort
      if (inventoryAdjustments.length === 0) {
        toast.warning('No adjustments to sync.');
        syncInFlightRef.current = false;
        setSyncingToUnicommerce(false);
        return;
      }

      // Call the correct Unicommerce inventory adjustment API
      // Log the payload for diagnostics (can remove later)
      const result = await WarehouseAPI.adjustInventoryOnUnicommerce(inventoryAdjustments, syncOperationId);
      syncOperationIdRef.current = null;

      // After successful Unicommerce update, log child SKU deductions to local backend (non-blocking)
      try {
        const childTotals: Array<{ sku: string; qty: number; shelfLocation?: string }> = [];
        Object.entries(state.childSKUPicking).forEach(([skuId, picking]) => {
          if (picking.picked && picking.shelfPickings.length > 0) {
            const totalPicked = picking.shelfPickings.reduce((sum: number, sp: any) => sum + (sp.quantityToPick || 0), 0);
            const shelfLoc = picking.shelfPickings[0]?.shelfLocation;
            if (totalPicked > 0) childTotals.push({ sku: skuId, qty: totalPicked, shelfLocation: shelfLoc });
          }
        });

        await Promise.all(childTotals.map(ct =>
          WarehouseAPI.logChildDeduction({
            sku: ct.sku,
            qty: ct.qty,
            bundleSku: state.selectedBundleSKU?.id,
            bundleQuantity: state.quantity,
            shelfLocation: ct.shelfLocation
          })
        ));
      } catch (logErr) {
        console.warn('Child deduction logging failed', logErr);
      }

      // Mark sync as completed
      setUnicommerceSyncCompleted(true);
      // Update the global state
      dispatch({ type: 'SET_UNICOMMERCE_SYNC_STATUS', payload: true });

      // Automatically save draft after successful sync
      try {
        await saveDraft(true);
      } catch (saveError) {
        console.warn('Auto-save draft failed after sync:', saveError);
        // We don't block the UI for this as the sync was successful
      }

      // Show success modal
      setUnicommerceResult({
        success: true,
        message: 'SKU inventory updated successfully on Unicommerce!',
        details: `Adjusted ${result.adjustedItems || inventoryAdjustments.length} inventory items. All picked quantities have been deducted from the warehouse system.`,
        adjustedCount: result.adjustedItems || inventoryAdjustments.length
      });
      setShowUnicommerceModal(true);
    } catch (error: any) {
      console.error('Error syncing to Unicommerce:', error);

      // Show error modal
      setUnicommerceResult({
        success: false,
        message: 'Failed to sync to Unicommerce',
        details: error.message,
        adjustedCount: 0
      });
      setShowUnicommerceModal(true);
    } finally {
      syncInFlightRef.current = false;
      setSyncingToUnicommerce(false);
    }
  };

  // Print pick list: Child SKU, Shelf location and Quantity
  const handlePrintPickList = () => {
    if (!state.selectedBundleSKU) return;

    const rows: Array<{ sku: string; shelf: string; qty: number }> = [];

    Object.entries(state.childSKUPicking).forEach(([skuId, picking]) => {
      if (picking && picking.shelfPickings && picking.shelfPickings.length > 0) {
        picking.shelfPickings.forEach((sp: any) => {
          if (sp.quantityToPick && sp.quantityToPick > 0) {
            // Find friendly name from selectedBundleSKU
            const skuMeta = aggregatedChildSKUs.find((c: any) => c.skuId === skuId);
            const skuName = skuMeta ? skuMeta.name : skuId;
            rows.push({ sku: skuName, shelf: sp.shelfLocation, qty: sp.quantityToPick });
          }
        });
      }
    });

    if (rows.length === 0) {
      toast.warning('No picked SKUs to print.');
      return;
    }

    const titleParts: string[] = [];
    if (includeBundleName) titleParts.push(state.selectedBundleSKU.name);
    if (includePickerName) titleParts.push((pickerName && pickerName.trim()) ? pickerName.trim() : (window.localStorage.getItem('bundleManagerUser') ? JSON.parse(window.localStorage.getItem('bundleManagerUser') || '{}').name : '') || '');
    const title = `Pick List${titleParts.length ? ' - ' + titleParts.filter(Boolean).join(' - ') : ''} - ${new Date().toLocaleString()}`;

    const style = `
      <style>
        body { font-family: Arial, Helvetica, sans-serif; padding: 24px; }
        h1 { font-size: 18px; margin-bottom: 8px; }
        table { width: 100%; border-collapse: collapse; margin-top: 12px; }
        th, td { border: 1px solid #333; padding: 8px; text-align: left; }
        th { background: #f2f2f2; }
      </style>
    `;

    const rowsHtml = rows.map(r => `
      <tr>
        <td>${r.sku}</td>
        <td>${r.shelf}</td>
        <td style="text-align: right">${r.qty}</td>
      </tr>
    `).join('\n');

    const metaHtmlParts: string[] = [];
    if (includeBundleName) metaHtmlParts.push(`<div><strong>Bundle:</strong> ${state.selectedBundleSKU.name}</div>`);
    metaHtmlParts.push(`<div><strong>Bundle SKU:</strong> ${state.selectedBundleSKU.id}</div>`);
    if (includePickerName) metaHtmlParts.push(`<div><strong>Picker:</strong> ${(pickerName && pickerName.trim()) ? pickerName.trim() : (window.localStorage.getItem('bundleManagerUser') ? JSON.parse(window.localStorage.getItem('bundleManagerUser') || '{}').name : '') || 'N/A'}</div>`);
    metaHtmlParts.push(`<div><strong>Printed At:</strong> ${new Date().toLocaleString()}</div>`);

    const html = `
      <html>
        <head>
          <title>${title}</title>
          ${style}
        </head>
        <body>
          <h1>${title}</h1>
          <div style="margin-bottom:12px;">${metaHtmlParts.join('')}</div>
          <table>
            <thead>
              <tr>
                <th>Child SKU</th>
                <th>Shelf Location</th>
                <th>Quantity</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.warning('Popup blocked. Please allow popups to print the pick list.');
      return;
    }

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    // Give browser a moment to render before printing
    setTimeout(() => {
      printWindow.print();
    }, 300);
  };



  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Assignment />
        Pick Child SKUs from Multiple Shelves
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select child SKUs and their shelf locations. You can pick from multiple shelves for the same SKU.
      </Typography>

      {/* Add New Shelf Picking */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Add />
            Add Shelf Picking
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Autocomplete
              value={selectedSKU}
              onChange={(_, newValue) => {
                setSelectedSKU(newValue);
                setSelectedShelf(null); // Reset shelf when SKU changes
              }}
              options={getAvailableSKUs()}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value?.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Child SKU"
                  size="small"
                  sx={{ minWidth: 250 }}
                />
              )}
              sx={{ flexGrow: 1 }}
            />

            <Autocomplete
              value={selectedShelf}
              onChange={(_, newValue) => {
                setSelectedShelf(newValue);
              }}
              options={selectedSKU ? getAvailableShelvesForSKU() : []}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value?.value}
              disabled={!selectedSKU}
              loading={!!(selectedSKU && isLoadingShelfData)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Shelf"
                  size="small"
                  sx={{ minWidth: 200 }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: <LocationOn color="action" sx={{ mr: 1 }} />,
                    endAdornment: (
                      <>
                        {selectedSKU && isLoadingShelfData ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  helperText={
                    selectedSKU && isLoadingShelfData
                      ? "Loading shelf locations..."
                      : selectedSKU && !isLoadingShelfData && getAvailableShelvesForSKU().length === 0
                        ? "No shelf locations found for this SKU"
                        : ""
                  }
                />
              )}
              sx={{ flexGrow: 1 }}
            />

            <TextField
              label="Quantity"
              type="number"
              value={quantityToPick}
              onChange={(e) => setQuantityToPick(e.target.value)}
              onBlur={(e) => {
                const value = parseInt(e.target.value) || 1;
                // Get max allowed for selected SKU
                const maxAllowed = selectedSKU ? (() => {
                  const picking = state.childSKUPicking[selectedSKU.value];
                  const childSKU = aggregatedChildSKUs.find((sku: any) => sku.skuId === selectedSKU.value);
                  const totalRequired = childSKU ? childSKU.requiredQuantity * state.quantity : 0;
                  const totalPicked = picking ? picking.totalPicked : 0;
                  return Math.max(1, totalRequired - totalPicked);
                })() : value;
                setQuantityToPick(Math.max(1, Math.min(value, maxAllowed)).toString());
              }}
              size="small"
              sx={{ width: 120 }}
              inputProps={{
                min: 1,
                max: selectedSKU ? (() => {
                  const picking = state.childSKUPicking[selectedSKU.value];
                  const childSKU = aggregatedChildSKUs.find((sku: any) => sku.skuId === selectedSKU.value);
                  const totalRequired = childSKU ? childSKU.requiredQuantity * state.quantity : 0;
                  const totalPicked = picking ? picking.totalPicked : 0;
                  return Math.max(1, totalRequired - totalPicked);
                })() : undefined
              }}
            />

            <Button
              variant="contained"
              onClick={handleAddShelfPicking}
              disabled={!selectedSKU || !selectedShelf || (parseInt(quantityToPick) || 0) <= 0}
              startIcon={<Add />}
            >
              Add
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Current Picking Status */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Picking Progress for: {state.selectedBundleSKU.name}
          </Typography>

          {aggregatedChildSKUs.map((childSKU: any) => {
            const picking = state.childSKUPicking[childSKU.skuId];
            const progress = getProgressForSKU(childSKU.skuId);
            const isComplete = picking && picking.totalPicked >= picking.totalRequired;

            return (
              <Box key={childSKU.skuId} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" fontWeight="medium">
                    {childSKU.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {picking?.totalPicked || 0} / {picking?.totalRequired || 0}
                    </Typography>
                    {isComplete && <CheckCircle color="success" fontSize="small" />}
                    {!isComplete && <Warning color="warning" fontSize="small" />}
                  </Box>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  color={isComplete ? 'success' : 'primary'}
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
            );
          })}
        </CardContent>
      </Card>

      {/* Detailed Picking Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Child SKU</TableCell>
              <TableCell>Shelf Location</TableCell>
              <TableCell align="right">Quantity to Pick</TableCell>
              <TableCell align="right">Available</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {aggregatedChildSKUs.map((childSKU: any) => {
              const picking = state.childSKUPicking[childSKU.skuId];

              if (!picking || picking.shelfPickings.length === 0) {
                return (
                  <TableRow key={childSKU.skuId}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {childSKU.name}
                      </Typography>
                    </TableCell>
                    <TableCell colSpan={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                        <Info fontSize="small" />
                        <Typography variant="caption">
                          No shelf pickings added yet. Required: {childSKU.requiredQuantity * state.quantity}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              }

              return picking.shelfPickings.map((shelfPicking, index) => (
                <TableRow key={`${childSKU.skuId}-${shelfPicking.shelfLocation}`}>
                  {index === 0 && (
                    <TableCell rowSpan={picking.shelfPickings.length}>
                      <Typography variant="body2" fontWeight="medium">
                        {childSKU.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total needed: {picking.totalRequired}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOn color="action" fontSize="small" />
                      <Typography variant="body2">
                        {shelfPicking.shelfLocation}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      value={shelfPicking.quantityToPick}
                      onChange={(e) => handleQuantityChange(
                        childSKU.skuId,
                        shelfPicking.shelfLocation,
                        parseInt(e.target.value) || 0
                      )}
                      size="small"
                      sx={{ width: 80 }}
                      inputProps={{
                        min: 0,
                        max: Math.min(
                          shelfPicking.availableQuantity,
                          picking.totalRequired - picking.totalPicked + shelfPicking.quantityToPick
                        )
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.secondary">
                      {shelfPicking.availableQuantity}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      onClick={() => handleRemoveShelfPicking(childSKU.skuId, shelfPicking.shelfLocation)}
                      color="error"
                      size="small"
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ));
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary Alert */}
      <Box sx={{ mt: 3 }}>
        <Alert severity={allRequiredPicked ? (state.unicommerceSyncCompleted ? 'success' : 'warning') : 'info'} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {allRequiredPicked
              ? (state.unicommerceSyncCompleted
                ? 'All requirements completed! You can now proceed to the next step.'
                : 'All SKUs picked. Please sync with Unicommerce to proceed.')
              : 'Complete picking all child SKUs to proceed.'
            }
          </Typography>
          {!allRequiredPicked && (
            <Box sx={{ mt: 1 }}>
              {aggregatedChildSKUs.map((childSKU: any) => {
                const picking = state.childSKUPicking[childSKU.skuId];
                const totalRequired = childSKU.requiredQuantity * state.quantity;
                const stillNeeded = picking ? picking.totalRequired - picking.totalPicked : totalRequired;

                if (stillNeeded > 0) {
                  return (
                    <Typography key={childSKU.skuId} variant="caption" component="div">
                      • {childSKU.name}: {stillNeeded} more needed
                    </Typography>
                  );
                }
                return null;
              })}
            </Box>
          )}
        </Alert>
      </Box>

      {/* Unicommerce Sync Section */}
      {allRequiredPicked && (
        <Card sx={{
          mt: 3,
          bgcolor: unicommerceSyncCompleted ? 'success.light' : 'primary.light',
          border: '2px solid',
          borderColor: unicommerceSyncCompleted ? 'success.main' : 'primary.main'
        }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
              {unicommerceSyncCompleted ? <CheckCircle sx={{ color: 'white' }} /> : <CloudUpload sx={{ color: 'white' }} />}
              {unicommerceSyncCompleted ? 'Updated Inventory on Unicommerce' : 'Update Inventory on Unicommerce (Required)'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: 'white' }}>
              {unicommerceSyncCompleted
                ? 'Inventory has been successfully synced with Unicommerce. You can now proceed to the next step.'
                : 'All child SKUs have been picked successfully. You must sync with Unicommerce to proceed to the next step.'
              }
            </Typography>

            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="contained"
                size="large"
                startIcon={
                  syncingToUnicommerce
                    ? <CircularProgress size={20} color="inherit" />
                    : unicommerceSyncCompleted
                      ? <CheckCircle />
                      : <CloudUpload />
                }
                onClick={handleSyncToUnicommerce}
                disabled={syncingToUnicommerce || unicommerceSyncCompleted}
                sx={{
                  minWidth: 250,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  backgroundColor: unicommerceSyncCompleted ? 'success.main' : undefined,
                  '&:hover': {
                    backgroundColor: unicommerceSyncCompleted ? 'success.dark' : undefined,
                  }
                }}
              >
                {syncingToUnicommerce
                  ? 'Syncing...'
                  : unicommerceSyncCompleted
                    ? 'Inventory Updated Successfully'
                    : 'Update picked SKU on Unicommerce'
                }
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<Print />}
                onClick={handlePrintPickList}
                sx={{ minWidth: 160 }}
              >
                Print Pick List
              </Button>
            </Stack>
            <Stack direction="row" spacing={2} sx={{ mt: 2, alignItems: 'center' }}>
              <FormControlLabel
                control={<Checkbox checked={includeBundleName} onChange={(e) => setIncludeBundleName(e.target.checked)} />}
                label="Include bundle name"
              />
              <FormControlLabel
                control={<Checkbox checked={includePickerName} onChange={(e) => setIncludePickerName(e.target.checked)} />}
                label="Include picker name"
              />
              {includePickerName && (
                <TextField
                  size="small"
                  label="Picker name"
                  value={pickerName}
                  onChange={(e) => {
                    setPickerName(e.target.value);
                    try { window.localStorage.setItem('lastPickerName', e.target.value); } catch { };
                  }}
                  sx={{ minWidth: 240 }}
                />
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Save Draft Section */}
      <Card sx={{ mt: 3, bgcolor: 'action.hover' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Save />
            Draft Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Save your picking progress as a draft to complete later.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<Save />}
              onClick={async () => {
                try {
                  await saveDraft();
                  toast.success('Draft saved successfully!');
                } catch (error) {
                  console.error('Error saving draft:', error);
                }
              }}
              disabled={state.savingDraft || Object.keys(state.childSKUPicking).length === 0}
              sx={{ minWidth: 120 }}
            >
              {state.savingDraft ? 'Saving...' : 'Save Draft'}
            </Button>
          </Box>

          {state.isDraft && state.draftId && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Draft ID: {state.draftId} | Status: {allRequiredPicked ? 'All items picked' : 'Partially picked'}
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Unicommerce Sync Result Modal */}
      <Dialog
        open={showUnicommerceModal}
        onClose={() => setShowUnicommerceModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: unicommerceResult?.success ? '#4caf50' : '#f44336',
          color: 'white'
        }}>
          {unicommerceResult?.success ? <CheckCircle /> : <Warning />}
          {unicommerceResult?.success ? 'Success!' : 'Error'}
        </DialogTitle>
        <DialogContent sx={{ pt: 4, pb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ mt: 1, mb: 2 }}>
            {unicommerceResult?.message}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {unicommerceResult?.details}
          </Typography>
          {unicommerceResult?.success && unicommerceResult.adjustedCount > 0 && (
            <Box sx={{
              mt: 2,
              p: 2,
              backgroundColor: 'rgba(76, 175, 80, 0.1)', // Light green background matching success theme
              border: '1px solid rgba(76, 175, 80, 0.3)',
              borderRadius: 1
            }}>
              <Typography variant="body2" sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'success.main' // Use theme success color
              }}>
                <Info sx={{ color: 'success.main' }} />
                <strong>Adjusted Items:</strong> {unicommerceResult.adjustedCount}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowUnicommerceModal(false)}
            variant="contained"
            color={unicommerceResult?.success ? "primary" : "error"}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChildSKUPicker;